import {
  Badge,
  Box,
  Chip,
  Divider,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Typography,
} from "@mui/material";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import React, { ReactNode, useContext } from "react";
import { useNavigate } from "react-router-dom";
import Asset1 from "./Asset1.png";
import SessionContext from "./SessionContext";
import { getSettingsItems, runSettingsItem, SettingsItemKey } from "./settingsItems";
import { PoloHorseIcon } from "../membership/PoloIcons";

const PAGE_ICONS: Record<string, ReactNode> = {
  Home: <HomeOutlinedIcon />,
  Players: <GroupsOutlinedIcon />,
  Horses: <PoloHorseIcon />,
};

const SETTINGS_ICONS: Record<SettingsItemKey, ReactNode> = {
  settings: <SettingsOutlinedIcon />,
  analytics: <InsightsOutlinedIcon />,
  managerGuide: <MenuBookOutlinedIcon />,
  playerGuide: <MenuBookOutlinedIcon />,
  logout: <LogoutOutlinedIcon />,
};

const itemSx = {
  borderRadius: 2,
  mx: 1,
  "&.Mui-selected": {
    bgcolor: "rgba(93, 138, 168, 0.14)",
    color: "primary.dark",
    "& .MuiListItemIcon-root": { color: "primary.dark" },
    "& .MuiListItemText-primary": { fontWeight: 600 },
  },
};

type NavDrawerProps = {
  open: boolean;
  onClose: () => void;
  pagesNav: Record<string, string>;
  waiverNotifications?: number;
  managerMode: number;
  betaMode: boolean;
  devPort: string;
};

function NavDrawer({
  open,
  onClose,
  pagesNav,
  waiverNotifications,
  managerMode,
  betaMode,
  devPort,
}: NavDrawerProps) {
  const navigate = useNavigate();
  const { lastManualClubId, setClubId } = useContext(SessionContext);
  const currentPath = window.location.pathname;

  function go(action: () => void) {
    onClose();
    action();
  }

  function goToPage(page: string) {
    if (page === "Home" && managerMode > -1) {
      setClubId(lastManualClubId);
    }
    navigate(pagesNav[page]);
  }

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: 280, borderTopRightRadius: 16, borderBottomRightRadius: 16 },
      }}
    >
      <Box data-testid="nav-drawer" sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            px: 2,
            py: 2,
            bgcolor: "primary.main",
            color: "white",
          }}
        >
          <img src={Asset1} alt="Chukkerbook logo" width={52} height={52} />
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700, lineHeight: 1.2 }}>Chukkerbook</Typography>
            <Chip
              size="small"
              label={managerMode === -1 ? "Manager" : "Player"}
              sx={{ mt: 0.5, height: 20, color: "white", bgcolor: "rgba(255, 255, 255, 0.2)" }}
            />
          </Box>
        </Box>

        <List subheader={<ListSubheader disableSticky>Menu</ListSubheader>}>
          {Object.keys(pagesNav).map((page) => (
            <ListItemButton
              key={page}
              selected={currentPath === pagesNav[page]}
              onClick={() => go(() => goToPage(page))}
              data-testid={"drawer-nav-" + page}
              sx={itemSx}
            >
              <ListItemIcon>{PAGE_ICONS[page]}</ListItemIcon>
              <ListItemText primary={page} />
              {page === "Players" && !!waiverNotifications && (
                <Badge badgeContent={waiverNotifications} color="secondary" sx={{ mr: 1 }} />
              )}
            </ListItemButton>
          ))}
        </List>

        <Divider sx={{ mx: 2 }} />

        <List subheader={<ListSubheader disableSticky>Account</ListSubheader>}>
          {getSettingsItems(managerMode, betaMode)
            .filter((item) => item.key !== "logout")
            .map((item) => (
              <ListItemButton
                key={item.key}
                selected={item.key === "settings" && currentPath === "/user-settings"}
                onClick={() => go(() => runSettingsItem(item.key, navigate, devPort))}
                data-testid={"drawer-settings-" + item.key}
                sx={itemSx}
              >
                <ListItemIcon>{SETTINGS_ICONS[item.key]}</ListItemIcon>
                <ListItemText primary={item.label} />
                {item.isNew && (
                  <Chip label="New" color="success" size="small" sx={{ height: 20 }} />
                )}
              </ListItemButton>
            ))}
        </List>

        <Box sx={{ mt: "auto", pb: 2 }}>
          <Divider sx={{ mx: 2, mb: 1 }} />
          <ListItemButton
            onClick={() => go(() => runSettingsItem("logout", navigate, devPort))}
            data-testid="drawer-settings-logout"
            sx={{ ...itemSx, color: "error.main" }}
          >
            <ListItemIcon sx={{ color: "error.main" }}>{SETTINGS_ICONS.logout}</ListItemIcon>
            <ListItemText primary="Logout" />
          </ListItemButton>
        </Box>
      </Box>
    </Drawer>
  );
}

export default NavDrawer;
